import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Upload, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { VideoPlayer } from '@/components/video-player/VideoPlayer';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { VideoFeatures } from '@/store/videoFeatures';
import { UploadQueueModal } from '@/components/upload/UploadQueueModal';

type VideoStatus = 'empty' | 'uploading' | 'processing' | 'ready' | 'error';

interface ErrorState {
  type: 'format' | 'size' | 'network' | 'processing';
  message: string;
}

interface VideoStageProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
  videoStatus: VideoStatus;
  setVideoStatus: (status: VideoStatus) => void;
  uploadProgress: number;
  setUploadProgress: (progress: number) => void;
  onReplaceVideo: () => void;
  onCopyEmbed: () => void;
  fileName?: string;
  fileSize?: string;
  error?: ErrorState | null;
  setError?: (error: ErrorState | null) => void;
}

export const VideoStage: React.FC<VideoStageProps> = ({
  features,
  setFeature,
  videoStatus,
  setVideoStatus,
  uploadProgress,
  setUploadProgress,
  onReplaceVideo,
  onCopyEmbed,
  fileName = '',
  fileSize = '',
  error = null,
  setError
}) => {
  useLanguage();
  const [isDragging, setIsDragging] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    // Validação de formato
    const validFormats = ['video/mp4', 'video/quicktime', 'video/webm'];
    if (!validFormats.includes(file.type)) {
      if (setError) {
        setError({
          type: 'format',
          message: translateInterfaceText("Formato não suportado. Use MP4, MOV ou WEBM.")
        });
      }
      setVideoStatus('error');
      return;
    }

    // Validação de tamanho (2GB)
    const maxSize = 2 * 1024 * 1024 * 1024;
    if (file.size > maxSize) {
      if (setError) {
        setError({
          type: 'size',
          message: translateInterfaceText("Seu arquivo excede 2GB. Envie um arquivo menor.")
        });
      }
      setVideoStatus('error');
      return;
    }

    // Limpar erro se existir
    if (setError) setError(null);
    
    // Iniciar upload
    setVideoStatus('uploading');
    simulateUpload(file);
  };

  const simulateUpload = (file: File) => {
    // Simular progresso de upload
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadProgress(progress);
      
      if (progress >= 100) {
        clearInterval(interval);
        // Após upload, iniciar processamento
        setVideoStatus('processing');
        
        // Simular processamento
        setTimeout(() => {
          // Criar URL do vídeo (em produção seria a URL do servidor)
          const videoUrl = URL.createObjectURL(file);
          setFeature('videoUrl', videoUrl);
          setVideoStatus('ready');
          toast.success(translateInterfaceText("Vídeo pronto para personalização!"));
        }, 2000);
      }
    }, 200);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleButtonClick = () => {
    setShowUploadModal(true);
  };

  const handlePersonalizeVideo = (videoUrl: string, videoName: string) => {
    setFeature('videoUrl', videoUrl);
    setVideoStatus('ready');
    setShowUploadModal(false);
    toast.success(translateInterfaceText("Vídeo \"{value0}\" pronto para personalização!", { value0: (videoName) }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleButtonClick();
    }
  };

  const handleRetry = () => {
    if (setError) setError(null);
    setVideoStatus('empty');
    setUploadProgress(0);
  };

  // Estado: Erro
  if (videoStatus === 'error' && error) {
    return (
      <div className="w-full max-w-[720px] mx-auto bg-white rounded-xl border border-border shadow-sm p-6 md:p-8"
           style={{ minHeight: '320px' }}>
        <div className="flex flex-col items-center justify-center h-full gap-6">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          
          <div className="text-center max-w-md">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {translateInterfaceText("Erro no upload")}</h3>
            <p className="text-sm text-muted-foreground" role="alert">
              {error.message}
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onReplaceVideo}
            >
              {translateInterfaceText("Trocar vídeo")}</Button>
            <Button onClick={handleRetry}>
              {translateInterfaceText("Tentar novamente")}</Button>
          </div>
        </div>
      </div>
    );
  }

  // Estado: Processando
  if (videoStatus === 'processing') {
    return (
      <div className="w-full max-w-[720px] mx-auto bg-white rounded-xl border border-border shadow-sm p-6 md:p-8"
           style={{ minHeight: '320px' }}>
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
          <div className="text-center">
            <h3 className="text-base font-semibold text-foreground mb-1">
              {translateInterfaceText("Processando vídeo...")}</h3>
            <p className="text-sm text-muted-foreground">
              {translateInterfaceText("Aguarde enquanto preparamos seu vídeo")}</p>
          </div>
        </div>
      </div>
    );
  }

  // Estado: Uploading
  if (videoStatus === 'uploading') {
    return (
      <div className="w-full max-w-[720px] mx-auto bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] shadow-[0px_4px_20px_rgba(0,0,0,0.04)] p-6 md:p-8"
           style={{ height: '320px' }}>
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <Loader2 className="w-12 h-12 text-[#316BFF] animate-spin" />
          <div className="text-center max-w-md w-full">
            <h3 className="text-[16px] font-semibold text-[#111827] mb-1">
              {translateInterfaceText("Enviando vídeo...")}</h3>
            {fileName && (
              <p className="text-sm text-muted-foreground mb-4">
                {fileName} {fileSize && `• ${fileSize}`}
              </p>
            )}
            <div className="space-y-2">
              <Progress value={uploadProgress} className="h-2 bg-[#E5E7EB] [&>div]:bg-[#316BFF]" />
              <p className="text-xs text-muted-foreground">
                {uploadProgress}{translateInterfaceText("% concluído")}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setVideoStatus('empty');
              setUploadProgress(0);
              toast.info(translateInterfaceText("Upload cancelado"));
            }}
          >
            {translateInterfaceText("Cancelar envio")}</Button>
        </div>
      </div>
    );
  }

  // Estado: Ready (com preview do player) - Visual Premium Compacto
  if (videoStatus === 'ready' && features.videoUrl) {
    return (
      <div className="w-full max-w-[820px] mx-auto relative px-4">
        <div
          className="w-full relative group"
        >
          <VideoPlayer features={features} isPreviewMode />
        </div>
      </div>
    );
  }

  // Estado: Empty (inicial - dropzone)
  return (
    <>
      <div
        className={cn(
          "w-[87%] mx-auto rounded-[22px] transition-all duration-300 cursor-pointer relative overflow-hidden",
          "md:w-[92%] lg:w-[87%]",
          isDragging 
            ? "border-[2px] border-[#2D6CF6]/60" 
            : "border border-[rgba(45,108,246,0.08)]"
        )}
        style={{ 
          minHeight: '420px',
          height: '420px',
          maxWidth: '1680px',
          background: isDragging 
            ? 'rgba(45,108,246,0.08)' 
            : 'linear-gradient(180deg, #FFFFFF 0%, #F5F9FF 100%)',
          boxShadow: isDragging 
            ? '0 22px 44px rgba(45,108,246,0.18), 0 0 0 1px rgba(45,108,246,0.3)' 
            : '0 14px 36px rgba(0,0,0,0.06)',
        }}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.boxShadow = '0 18px 42px rgba(45,108,246,0.12)';
            e.currentTarget.style.borderColor = 'rgba(45,108,246,0.15)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            e.currentTarget.style.boxShadow = '0 14px 36px rgba(0,0,0,0.06)';
            e.currentTarget.style.borderColor = 'rgba(45,108,246,0.08)';
          }
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={translateInterfaceText("Área de upload de vídeo. Pressione Enter para selecionar um arquivo")}
      >
        {/* HostVSL Aura - Linha Lateral Brilhante */}
        <div 
          className="absolute left-0 top-0 w-[3px] h-full rounded-l-[22px] opacity-45"
          style={{
            background: 'linear-gradient(180deg, #2D6CF6, #5B9EFF)',
            animation: 'breathing 3s ease-in-out infinite'
          }}
        />
        <div className="flex flex-col items-center justify-center h-full gap-3 px-8 py-8">
          <div 
            className="flex items-center justify-center group"
            style={{ animation: 'float 3s ease-in-out infinite' }}
          >
            <Upload 
              className="w-16 h-16 transition-all duration-300" 
              strokeWidth={2}
              style={{
                color: '#2D6CF6',
                filter: 'drop-shadow(0 4px 12px rgba(45,108,246,0.15))'
              }}
            />
          </div>
          
          <div className="text-center mt-4">
            <h3 className="text-[20px] font-semibold text-[#1E2036] mb-3 tracking-tight">
              {translateInterfaceText("Envie seu vídeo")}</h3>
            <p className="text-[14px] text-[#6A6F85] max-w-[420px] leading-relaxed mx-auto">
              {translateInterfaceText("Arraste seu vídeo aqui ou clique no botão abaixo para selecionar")}</p>
          </div>
          
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleButtonClick();
            }}
            className="mt-4 h-11 px-7 rounded-xl font-semibold text-[15px] text-white border-none shadow-lg transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 focus:ring-0 focus:ring-offset-0"
            style={{
              background: '#2D6CF6',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(45deg, #2D6CF6, #5B9EFF)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#2D6CF6';
            }}
          >
            {translateInterfaceText("Selecionar vídeo")}</Button>
          
          <p className="text-[13px] text-[#6A6F85]/70 mt-4 font-medium">
            {translateInterfaceText("MP4, MOV, WEBM • Máximo 2GB")}</p>
        </div>
      </div>

      {/* Modal de Upload Múltiplo */}
      <UploadQueueModal
        open={showUploadModal}
        onOpenChange={setShowUploadModal}
        onPersonalize={handlePersonalizeVideo}
      />
    </>
  );
};
